import { Campaign } from '@/types/campaign';
import { api } from './client';
import type { Participant, AddParticipant, ParticipantContext } from '@/types/participant';

export const participationsApi = {
  getAll: async (params: ParticipantContext, isOnboarded: boolean): Promise<Participant[]> => {
    if (isOnboarded) {
      return fakeParticipations as Participant[];
    }

    const { data } = await api.get(`/companies/${params.organisationId}/workspaces/${params.spaceId}/campaigns/${params.campaignId}/participants`);
    return data.participants;
  },

  // get: async (participationId: string): Promise<Participation> => {
  //   const { data } = await api.get(`/api/v1/participations/${participationId}`);
  //   return data;
  // }

  getParticipationsCompatibility: async (participation: Participant, campaign: Campaign): Promise<number> => {
    try {
      const { data } = await api.post('/api/v1/assessments/compatibility', {
        assessments: [
          {
            "id": "00000000-0000-0000-0000-000000000000",
            "created_at": "2023-08-11T06:24:59+02:00",
            "updated_at": "2023-08-11T06:24:59+02:00",
            "account_id": "00000000-0000-0000-0000-000000000000",
            "cloud_sessions": null,
            "started_at": "2023-08-11T06:24:59+02:00",
            "trackers": {
              "version": "0.0.0",
              "session_id": "00000000-0000-0000-0000-000000000000",
              "total_modules": 1,
              "completed_modules": 1
            },
            "completed_at": participation.results.completed_at,
            "achievements": participation.results,
          }
        ],
        campaign: campaign,
      }, { headers: { 'Content-Type': 'application/json' } });
  
      return data.compatibility_scores[0].compatibility_score;
    } catch (error) {
      throw error;
    }
  },

  delete: async (params: ParticipantContext, participantIds: string[]): Promise<{ code: number, key: string }> => {
    try {
      const { data } = await api.delete(`/companies/${params.organisationId}/workspaces/${params.spaceId}/campaigns/${params.campaignId}/participants`, { data: { participants: participantIds } });
      return data;
    } catch (error) {
      throw error;
    }
  },

  activateParticipants: async (params: ParticipantContext, participantIds: string[]): Promise<{ code: number, key: string }> => {
    try {
      const { data } = await api.put(`/companies/${params.organisationId}/workspaces/${params.spaceId}/campaigns/${params.campaignId}/participants`, { participants: participantIds });
      return data;
    } catch (error) {
      throw error;
    }
  },

  desactivateParticipants: async (params: ParticipantContext, participantIds: string[]): Promise<{ code: number, key: string }> => {
    try {
      const { data } = await api.put(`/companies/${params.organisationId}/workspaces/${params.spaceId}/campaigns/${params.campaignId}/participants/activations`, { participants: participantIds });
      return data;
    } catch (error) {
      throw error;
    }
  },

  moveParticipants: async (params: ParticipantContext, participantIds: string[], campaignId: string): Promise<{ code: number, key: string }> => {
    try {
      const { data } = await api.put(`/companies/${params.organisationId}/workspaces/${params.spaceId}/campaigns/${params.campaignId}/participants-move`, { participants: participantIds, target_campaign_id: campaignId });
      return data;
    } catch (error) {
      throw error;
    }
  },

  commentParticipant: async (params: ParticipantContext, participantId: string, comment: string): Promise<{ code: number, key: string }> => {
    try {
      const { data } = await api.put(`/companies/${params.organisationId}/workspaces/${params.spaceId}/campaigns/${params.campaignId}/participants/${participantId}/comment`, { user_comment: comment });
      return data;
    } catch (error) {
      throw error;
    }
  },

  sendReminderToParticipant: async (params: ParticipantContext, participantId: string): Promise<{ code: number, key: string, sent: boolean }> => {
    try {
      const { data } = await api.post(`/companies/${params.organisationId}/workspaces/${params.spaceId}/campaigns/${params.campaignId}/participants/${participantId}/reminders`, {}, { headers: { 'Content-Type': 'multipart/form-data' } });
      return data;
    } catch (error) {
      throw error;
    }
  }
}

export const fakeResults = {
  "version": "1.0.0",
  "campaign_version": "3.0.0", // idem pour 2.1.2
  "scores": {
      "adaptability": 0.2000,
      "active_listening": 0.4000,
      "empathy": 0.4000,
      "time_management": 0.7524,
      "stress_management": 0.7000,
      "decision_making": 1.0000,
      "problem_solving": 0.1111,
      "social_desirability": 0.4667,
      "leadership": {
          "laissez_faire": 0.7500,
          "transactional": 0.7500,
          "transformational": 0.7708
      },
      "planning": 0.6528
  },
  "styles": {
      "leadership": "transformational",
      "motivation": "intrinsic",
      "learning_strategy": "critical_thinking"
  },
  "badges": {
      "altruism": false,
      "autonomy": true,
      "cooperation": false,
      "curiosity": false,
      "willingness_to_learn": false,
      "diplomacy": true,
      "efficiency": false,
      "innovation": false,
      "leadership_adaptability": true,
      "optimism": false,
      "perseverance": false,
      "humor": true,
      "positivity": true,
      "pragmatism": false,
      "social_desirability": false,
      "boldness": false,
  },
  "compatibility": 0.6
}

const fakeResults2 = {
  ...fakeResults,
  compatibility: 0.81,
  "scores": {
      "adaptability": 0.8000,
      "active_listening": 0.7000,
      "empathy": 0.9000,
      "time_management": 0.9524,
      "stress_management": 0.1000,
      "decision_making": 0.6000,
      "problem_solving": 0.8111,
      "social_desirability": 0.7667,
      "leadership": {
          "laissez_faire": 0.2500,
          "transactional": 0.5500,
          "transformational": 0.7708
      },
      "planning": 0.3528
  },
}

const fakeResults3 = {
  ...fakeResults,
  compatibility: 0.54,
  "scores": {
      "adaptability": 0.5000,
      "active_listening": 0.6000,
      "empathy": 0.7000,
      "time_management": 0.3524,
      "stress_management": 0.6000,
      "decision_making": 0.5000,
      "problem_solving": 0.4111,
      "social_desirability": 0.1667,
      "leadership": {
          "laissez_faire": 0.2500,
          "transactional": 0.5500,
          "transformational": 0.7708
      },
      "planning": 0.5528
  },
}

const fakeParticipations = [
    // To active
    {
      creation_time: "2023-08-11T06:24:59+02:00Z",
      activated: false,
      state: 0,
      candidate_id: 1,
      candidate: {
        first_name: "Léna",
        last_name: "Carcone",
        email: "léna.carcone@gmail.com",
      },
    },
    {
      pending_first_name: "Nolwenn",
      pending_last_name: "Boduch",
      pending_email: "nolwenn.boduch@gmail.com",
      creation_time: "2023-08-10T06:24:59+02:00Z",
      activated: false,
      state: 0,
    },
    {
      creation_time: "2023-08-09T06:24:59+02:00Z",
      activated: false,
      state: 0,
      candidate_id: 1,
      candidate: {
        first_name: "Raphaël",
        last_name: "Poirier",
        email: "poirier.raphael@gmail.com",
      },
    },
    {
      pending_first_name: "Joris",
      pending_last_name: "André",
      pending_email: "j.andre@gmail.com",
      creation_time: "2023-08-11T06:24:59+02:00Z",
      activated: false,
      state: 0,
    },
    {
      creation_time: "2023-08-12T06:24:59+02:00Z",
      activated: false,
      candidate_id: 1,
      candidate: {
        first_name: "Ana",
        last_name: "Lecacheur",
        email: "ana.lecacheur@gmail.com",
      },
      state: 0,
    },
    {
      pending_first_name: "Anthonin",
      pending_last_name: "Chardin",
      pending_email: "anthonin.chardin@gmail.com",
      creation_time: "2023-08-12T06:24:59+02:00Z",
      activated: false,
      state: 0,
    },
    // In progress
    {
      creation_time: "2023-08-11T06:24:59+02:00Z",
      activated: true,
      progress: 0,
      state: 0,
      candidate_id: 1,
      candidate: {
        first_name: "Lucas",
        last_name: "Desmaisons",
        email: "desmaisons.lucas@gmail.com",
      },
    },
    {
      pending_first_name: "Noémie",
      pending_last_name: "Monrocq",
      pending_email: "n.monrocq@gmail.com",
      creation_time: "2023-08-12T06:24:59+02:00Z",
      activated: true,
      progress: 33,
      state: 1,
    },
    // Results
    {
      candidate_id: 1,
      state: 2,
      participant_id: 1,
      candidate: {
        first_name: "Manon",
        last_name: "Carré",
        email: "test@gmail.com",
      },
      creation_time: "2023-08-12T06:24:59+02:00Z",
      results: fakeResults,
    },
    {
      candidate_id: 1,
      state: 2,
      participant_id: 2,
      candidate: {
        first_name: "Antoine",
        last_name: "Albo",
        email: "antoine.albo@gmail.com",
      },
      creation_time: "2023-08-12T06:24:59+02:00Z",
      results: fakeResults2,
    },
    {
      candidate_id: 1,
      state: 2,
      participant_id: 3,
      candidate: {
        first_name: "Léa",
        last_name: "Toussaint",
        email: "lea.toussaint@gmail.com",
      },
      creation_time: "2023-08-12T06:24:59+02:00Z",
      results: fakeResults3,
    },
    {
      candidate_id: 1,
      state: 2,
      candidate: {
        first_name: "Harry",
        last_name: "Maguire",
        email: "harry.maguire@gmail.com",
      },
      creation_time: "2023-08-11T06:24:59+02:00Z",
      results: fakeResults,
    },
    {
      candidate_id: 1,
      state: 2,
      candidate: {
        first_name: "Denise",
        last_name: "Soto",
        email: "denise.soto@gmail.com",
      },
      creation_time: "2023-08-09T06:24:59+02:00Z",
      results: fakeResults2,
    },
  ]